import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Newspaper, Package, Pencil, Plus, Trash2, Upload, Mail, Gift, TicketCheck, LayoutDashboard, TrendingUp, Star } from "lucide-react";
import { toast } from "sonner";
import client, { imgSrc } from "@/lib/api";
import { CATEGORIES, CATEGORY_LABELS, fmtPrice } from "@/lib/constants";

const emptyProduct = { name: "", category: "anelli", price: "", old_price: "", material: "", description: "", image: "", images: [], featured: false, available: true };
const emptyPost = { title: "", excerpt: "", content: "", image: "" };

function ImageField({ value, onChange, testid }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await client.post("/upload", fd);
      onChange(data.url);
      toast.success("Immagine caricata");
    } catch {
      toast.error("Caricamento immagine non riuscito");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] text-[#A3A3A3]">Immagine</label>
      <div className="mt-2 flex items-center gap-3">
        {value && (
          <img src={imgSrc(value)} alt="" className="h-14 w-14 border border-white/10 object-cover" />
        )}
        <input
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="/assets/products/… oppure carica"
          className="w-full border border-white/15 bg-transparent px-3 py-2.5 text-sm"
        />
        <button
          type="button"
          data-testid={`${testid}-upload-button`}
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex shrink-0 items-center gap-2 border border-white/15 px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors hover:border-gold hover:text-gold"
        >
          <Upload size={13} /> {uploading ? "…" : "Carica"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`${testid}-file-input`} />
      </div>
    </div>
  );
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid={testid}>
      <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto border border-white/10 bg-[#0d0d0d] p-8">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-2xl">{title}</h3>
          <button onClick={onClose} data-testid={`${testid}-close-button`} className="text-[#A3A3A3] transition-colors hover:text-gold">
            ✕
          </button>
        </div>
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}

const inputCls = "mt-2 w-full border border-white/15 bg-transparent px-3 py-2.5 text-sm";
const labelCls = "text-[10px] uppercase tracking-[0.25em] text-[#A3A3A3]";

function GalleryField({ images, onChange, testid }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [path, setPath] = useState("");

  const uploadMany = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const added = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await client.post("/upload", fd);
        added.push(data.url);
      } catch {
        toast.error(`Caricamento non riuscito: ${file.name}`);
      }
    }
    if (added.length) {
      onChange([...images, ...added]);
      toast.success(`${added.length} foto aggiunte alla galleria`);
    }
    setUploading(false);
    e.target.value = "";
  };

  const addPath = () => {
    const p = path.trim();
    if (!p) return;
    onChange([...images, p]);
    setPath("");
  };

  return (
    <div>
      <label className={labelCls}>Galleria foto (la prima è la copertina)</label>
      <div className="mt-2 grid grid-cols-4 gap-2" data-testid={testid}>
        {images.map((img, i) => (
          <div key={img + i} className="group relative" data-testid={`${testid}-item-${i}`}>
            <img src={imgSrc(img)} alt="" className="aspect-square w-full border border-white/10 object-cover" />
            {i === 0 && (
              <span className="absolute left-1 top-1 bg-gold px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-widest text-black">Cover</span>
            )}
            <button
              type="button"
              onClick={() => onChange(images.filter((_, x) => x !== i))}
              data-testid={`${testid}-remove-${i}`}
              aria-label="Rimuovi foto"
              className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center bg-black/70 text-xs text-red-300 opacity-0 transition-opacity group-hover:opacity-100"
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`${testid}-upload-button`}
          className="flex aspect-square w-full flex-col items-center justify-center gap-1 border border-dashed border-white/20 text-[#A3A3A3] transition-colors hover:border-gold hover:text-gold"
        >
          <Upload size={16} />
          <span className="text-[9px] uppercase tracking-widest">{uploading ? "…" : "Carica"}</span>
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={uploadMany} data-testid={`${testid}-file-input`} />
      </div>
      <div className="mt-2 flex gap-2">
        <input
          data-testid={`${testid}-path-input`}
          value={path}
          onChange={(e) => setPath(e.target.value)}
          placeholder="…oppure incolla percorso immagine"
          className="w-full border border-white/15 bg-transparent px-3 py-2 text-xs"
        />
        <button
          type="button"
          onClick={addPath}
          data-testid={`${testid}-add-path-button`}
          className="shrink-0 border border-white/15 px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors hover:border-gold hover:text-gold"
        >
          <Plus size={13} />
        </button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("panoramica");
  const [products, setProducts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [spins, setSpins] = useState([]);
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState({ author: "", text: "" });
  const [verifyCode, setVerifyCode] = useState("");
  const [verifyResult, setVerifyResult] = useState(null);
  const [editing, setEditing] = useState(null); // {type:'product'|'post', data, id?}
  const [siteTexts, setSiteTexts] = useState(null); // {values, defaults, labels}
  const [savingTexts, setSavingTexts] = useState(false);

  const loadTexts = useCallback(async () => {
    try {
      const { data } = await client.get("/admin/site-texts");
      setSiteTexts(data);
    } catch {
      /* silenzioso */
    }
  }, []);

  const saveTexts = async () => {
    setSavingTexts(true);
    try {
      await client.put("/admin/site-texts", { values: siteTexts.values });
      toast.success("Testi del sito salvati e visibili subito online");
    } catch {
      toast.error("Salvataggio testi non riuscito");
    } finally {
      setSavingTexts(false);
    }
  };

  const load = useCallback(async () => {
    try {
      await client.get("/auth/me");
    } catch {
      localStorage.removeItem("oj_token");
      navigate("/admin");
      return;
    }
    const [p, n, m, s, st, rv] = await Promise.all([
      client.get("/admin/products").catch(() => ({ data: [] })),
      client.get("/posts").catch(() => ({ data: [] })),
      client.get("/admin/messages").catch(() => ({ data: [] })),
      client.get("/admin/promo").catch(() => ({ data: [] })),
      client.get("/admin/promo/stats").catch(() => ({ data: null })),
      client.get("/reviews").catch(() => ({ data: { reviews: [] } })),
    ]);
    setProducts(p.data);
    setPosts(n.data);
    setMessages(m.data);
    setSpins(s.data);
    setStats(st.data);
    setReviews(rv.data.reviews || []);
  }, [navigate]);

  useEffect(() => {
    load();
    loadTexts();
  }, [load, loadTexts]);

  const logout = () => {
    localStorage.removeItem("oj_token");
    navigate("/admin");
  };

  const redeemSpin = async (code) => {
    try {
      await client.post("/admin/promo/redeem", { code });
      toast.success(`Coupon ${code} segnato come riscattato`);
      load();
    } catch {
      toast.error("Codice non trovato");
    }
  };

  const verifySpin = async (e) => {
    e.preventDefault();
    if (!verifyCode.trim()) return;
    try {
      const { data } = await client.get("/admin/promo/verify", { params: { code: verifyCode.trim() } });
      setVerifyResult(data);
    } catch {
      setVerifyResult({ invalid: true });
    }
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    const d = editing.data;
    const payload = {
      ...d,
      price: parseFloat(d.price) || 0,
      old_price: d.old_price ? parseFloat(d.old_price) : null,
    };
    if (payload.images?.length) payload.image = payload.images[0];
    try {
      if (editing.id) await client.put(`/admin/products/${editing.id}`, payload);
      else await client.post("/admin/products", payload);
      toast.success(editing.id ? "Prodotto aggiornato" : "Prodotto creato");
      setEditing(null);
      load();
    } catch {
      toast.error("Salvataggio non riuscito");
    }
  };

  const savePost = async (e) => {
    e.preventDefault();
    try {
      if (editing.id) await client.put(`/admin/posts/${editing.id}`, editing.data);
      else await client.post("/admin/posts", editing.data);
      toast.success(editing.id ? "Articolo aggiornato" : "Articolo creato");
      setEditing(null);
      load();
    } catch {
      toast.error("Salvataggio non riuscito");
    }
  };

  const remove = async (type, id) => {
    if (!window.confirm("Sei sicuro? L'operazione non è reversibile.")) return;
    try {
      await client.delete(`/admin/${type}/${id}`);
      toast.success("Eliminato");
      load();
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  const TABS = [
    ["panoramica", "Panoramica", LayoutDashboard, null],
    ["prodotti", "Prodotti", Package, products.length],
    ["novita", "Novità", Newspaper, posts.length],
    ["messaggi", "Messaggi", Mail, messages.length],
    ["promo", "Promo", Gift, spins.length],
    ["recensioni", "Recensioni", Star, reviews.length],
    ["testi", "Testi del sito", Pencil, null],
  ];

  return (
    <main className="min-h-screen bg-[#050505] pb-20 pt-8" data-testid="admin-dashboard">
      <div className="noise-overlay" aria-hidden="true" />
      <div className="mx-auto max-w-6xl px-5">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-6">
          <div className="flex items-center gap-4">
            <img src="/assets/logo-gold.png" alt="Oro Jewels — Gioielli & Orologi" className="h-10 w-auto" />
            <div>
              <h1 className="font-serif text-xl">Gestione vetrina</h1>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#737373]">Oro Jewels · Admin</p>
            </div>
          </div>
          <div className="flex gap-3">
            <a href="/" data-testid="admin-view-site-link" className="border border-white/15 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors hover:border-gold hover:text-gold">
              Vedi il sito
            </a>
            <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 border border-white/15 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors hover:border-red-800 hover:text-red-300">
              <LogOut size={13} /> Esci
            </button>
          </div>
        </div>

        <div className="mt-8 flex gap-2" data-testid="admin-tabs">
          {TABS.map(([key, label, Icon, count]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] transition-colors ${
                tab === key ? "bg-gold text-black" : "border border-white/15 text-[#A3A3A3] hover:border-gold/60 hover:text-gold"
              }`}
            >
              <Icon size={13} /> {label}{count !== null ? ` · ${count}` : ""}
            </button>
          ))}
        </div>

        {tab === "panoramica" && (
          <section className="mt-8" data-testid="admin-overview-section">
            <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { label: "Giri ruota oggi", value: stats?.today ?? 0, icon: Gift, testid: "stat-spins-today" },
                { label: "Giri totali", value: stats?.total ?? 0, icon: TrendingUp, testid: "stat-spins-total" },
                { label: "Premi riscossi", value: stats?.redeemed ?? 0, icon: TicketCheck, testid: "stat-redeemed" },
                { label: "Prodotti in vetrina", value: products.length, icon: Package, testid: "stat-products" },
              ].map((c) => (
                <div key={c.label} className="bg-[#050505] p-6" data-testid={c.testid}>
                  <c.icon size={16} className="text-gold" />
                  <p className="mt-4 font-serif text-4xl text-[#FAFAFA]">{c.value}</p>
                  <p className="mt-2 text-[10px] uppercase tracking-[0.25em] text-[#737373]">{c.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 grid gap-8 lg:grid-cols-2">
              <div className="border border-white/10 p-6" data-testid="admin-chart-spins-by-day">
                <h3 className="text-[10px] uppercase tracking-[0.3em] text-[#737373]">Giri della ruota per giorno · ultimi 14 giorni</h3>
                {(() => {
                  const countMap = Object.fromEntries((stats?.by_day || []).map((d) => [d.day, d.count]));
                  const days = [...Array(14)].map((_, i) => {
                    const d = new Date();
                    d.setDate(d.getDate() - (13 - i));
                    return d.toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });
                  });
                  const max = Math.max(1, ...days.map((d) => countMap[d] || 0));
                  return (
                    <div className="mt-6 flex items-end gap-1.5">
                      {days.map((d) => {
                        const c = countMap[d] || 0;
                        const isToday = d === days[13];
                        return (
                          <div key={d} className="flex flex-1 flex-col items-center" data-testid={`chart-day-${d}`}>
                            <span className="mb-1 text-[9px] text-[#A3A3A3]">{c > 0 ? c : ""}</span>
                            <div className="flex h-28 w-full items-end">
                              <div
                                className={`w-full transition-all ${isToday ? "bg-gold" : "bg-gold/40"}`}
                                style={{ height: `${c > 0 ? Math.max(4, (c / max) * 100) : 0}%` }}
                                title={`${d}: ${c} giri`}
                              />
                            </div>
                            <span className="mt-2 text-[8px] uppercase tracking-wide text-[#737373]">
                              {d.slice(8)}/{d.slice(5, 7)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              <div className="border border-white/10 p-6" data-testid="admin-chart-prizes">
                <h3 className="text-[10px] uppercase tracking-[0.3em] text-[#737373]">Premi vinti</h3>
                {(() => {
                  const rows = stats?.by_prize || [];
                  const max = Math.max(1, ...rows.map((r) => r.count));
                  if (rows.length === 0)
                    return <p className="mt-6 text-sm text-[#737373]" data-testid="prizes-empty">Nessun premio ancora assegnato.</p>;
                  return (
                    <div className="mt-6 space-y-4">
                      {rows.map((r) => (
                        <div key={r.prize} data-testid={`prize-row-${r.prize.slice(0, 20).replace(/\s/g, "-")}`}>
                          <div className="flex items-baseline justify-between gap-4">
                            <p className="truncate text-xs text-[#D4D4D4]">{r.prize}</p>
                            <p className="shrink-0 text-xs text-[#A3A3A3]">
                              <span className="text-gold">{r.count}</span> vinti · {r.redeemed} riscossi
                            </p>
                          </div>
                          <div className="mt-1.5 h-1.5 w-full bg-white/5">
                            <div className="h-full bg-gold/70" style={{ width: `${(r.count / max) * 100}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          </section>
        )}

        {tab === "prodotti" && (
          <section className="mt-8" data-testid="admin-products-section">
            <button
              onClick={() => setEditing({ type: "product", data: { ...emptyProduct } })}
              data-testid="admin-new-product-button"
              className="flex items-center gap-2 bg-gold px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-black transition-colors hover:bg-gold-hover"
            >
              <Plus size={13} /> Nuovo prodotto
            </button>
            <div className="mt-6 overflow-x-auto border border-white/10">
              <table className="w-full text-left text-sm" data-testid="admin-products-table">
                <thead>
                  <tr className="border-b border-white/10 text-[10px] uppercase tracking-[0.25em] text-[#737373]">
                    <th className="px-4 py-3">Prodotto</th>
                    <th className="px-4 py-3">Categoria</th>
                    <th className="px-4 py-3">Prezzo</th>
                    <th className="px-4 py-3">In evidenza</th>
                    <th className="px-4 py-3">Visibile</th>
                    <th className="px-4 py-3 text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-b border-white/5 transition-colors hover:bg-white/[0.02]" data-testid={`admin-product-row-${p.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {p.image && <img src={imgSrc(p.image)} alt="" className="h-10 w-10 bg-[#0a0a0a] object-cover" />}
                          <span className="font-medium">{p.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[#A3A3A3]">{CATEGORY_LABELS[p.category] || p.category}</td>
                      <td className="px-4 py-3 text-gold">€ {fmtPrice(p.price)}</td>
                      <td className="px-4 py-3">{p.featured ? "Sì" : "No"}</td>
                      <td className="px-4 py-3">{p.available ? "Sì" : "No"}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => setEditing({ type: "product", id: p.id, data: { ...p, price: p.price, old_price: p.old_price ?? "", images: p.images?.length ? p.images : p.image ? [p.image] : [] } })}
                            data-testid={`admin-product-edit-${p.id}`}
                            className="border border-white/15 p-2 text-[#A3A3A3] transition-colors hover:border-gold hover:text-gold"
                            aria-label="Modifica"
                          >
                            <Pencil size={13} />
                          </button>
                          <button
                            onClick={() => remove("products", p.id)}
                            data-testid={`admin-product-delete-${p.id}`}
                            className="border border-white/15 p-2 text-[#A3A3A3] transition-colors hover:border-red-800 hover:text-red-300"
                            aria-label="Elimina"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "novita" && (
          <section className="mt-8" data-testid="admin-posts-section">
            <button
              onClick={() => setEditing({ type: "post", data: { ...emptyPost } })}
              data-testid="admin-new-post-button"
              className="flex items-center gap-2 bg-gold px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-black transition-colors hover:bg-gold-hover"
            >
              <Plus size={13} /> Nuovo articolo
            </button>
            <div className="mt-6 space-y-3">
              {posts.map((p) => (
                <div key={p.id} className="flex flex-wrap items-center justify-between gap-4 border border-white/10 p-4" data-testid={`admin-post-row-${p.id}`}>
                  <div className="flex items-center gap-4">
                    {p.image && <img src={imgSrc(p.image)} alt="" className="h-12 w-12 bg-ivory object-cover" />}
                    <div>
                      <p className="font-medium">{p.title}</p>
                      <p className="text-xs text-[#737373]">{new Date(p.published_at).toLocaleDateString("it-IT")}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditing({ type: "post", id: p.id, data: { title: p.title, excerpt: p.excerpt || "", content: p.content, image: p.image || "" } })}
                      data-testid={`admin-post-edit-${p.id}`}
                      className="border border-white/15 p-2 text-[#A3A3A3] transition-colors hover:border-gold hover:text-gold"
                      aria-label="Modifica"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      onClick={() => remove("posts", p.id)}
                      data-testid={`admin-post-delete-${p.id}`}
                      className="border border-white/15 p-2 text-[#A3A3A3] transition-colors hover:border-red-800 hover:text-red-300"
                      aria-label="Elimina"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {tab === "messaggi" && (
          <section className="mt-8 space-y-3" data-testid="admin-messages-section">
            {messages.length === 0 && (
              <p className="border border-white/10 p-8 text-center text-sm text-[#737373]" data-testid="admin-messages-empty">
                Nessun messaggio ricevuto.
              </p>
            )}
            {messages.map((m) => (
              <div key={m.id} className="border border-white/10 p-5" data-testid={`admin-message-row-${m.id}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-medium">{m.name} <span className="text-sm font-normal text-[#737373]">· {m.email}{m.phone ? ` · ${m.phone}` : ""}</span></p>
                  <div className="flex items-center gap-3">
                    <p className="text-xs text-[#737373]">{new Date(m.created_at).toLocaleString("it-IT")}</p>
                    <button
                      onClick={() => remove("messages", m.id)}
                      data-testid={`admin-message-delete-${m.id}`}
                      className="border border-white/15 p-1.5 text-[#A3A3A3] transition-colors hover:border-red-800 hover:text-red-300"
                      aria-label="Elimina"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-[#A3A3A3]">{m.message}</p>
              </div>
            ))}
          </section>
        )}
        {tab === "recensioni" && (
          <section className="mt-8" data-testid="admin-reviews-section">
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!newReview.author.trim() || !newReview.text.trim()) return;
                try {
                  await client.post("/admin/reviews", newReview);
                  toast.success("Recensione aggiunta");
                  setNewReview({ author: "", text: "" });
                  load();
                } catch {
                  toast.error("Salvataggio non riuscito");
                }
              }}
              className="grid gap-4 border border-white/10 p-5 sm:grid-cols-[220px_1fr_auto]"
              data-testid="admin-review-form"
            >
              <input
                data-testid="admin-review-author"
                value={newReview.author}
                onChange={(e) => setNewReview({ ...newReview, author: e.target.value })}
                placeholder="Nome cliente"
                className="border border-white/15 bg-transparent px-3 py-2.5 text-sm"
              />
              <input
                data-testid="admin-review-text"
                value={newReview.text}
                onChange={(e) => setNewReview({ ...newReview, text: e.target.value })}
                placeholder="Testo della recensione a 5 stelle"
                className="border border-white/15 bg-transparent px-3 py-2.5 text-sm"
              />
              <button
                type="submit"
                data-testid="admin-review-submit"
                className="flex items-center gap-2 bg-gold px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-black transition-colors hover:bg-gold-hover"
              >
                <Plus size={13} /> Aggiungi
              </button>
            </form>
            <p className="mt-3 text-xs leading-relaxed text-[#737373]">
              Sul sito vengono mostrate solo recensioni a 5 stelle. Valutazione e numero totale si aggiornano automaticamente da Google ogni 3 giorni (richiede la chiave Google Places).
            </p>
            <div className="mt-6 space-y-3">
              {reviews.length === 0 && (
                <p className="border border-white/10 p-8 text-center text-sm text-[#737373]" data-testid="admin-reviews-empty">Nessuna recensione.</p>
              )}
              {reviews.map((r) => (
                <div key={r.id} className="flex items-start justify-between gap-4 border border-white/10 p-4" data-testid={`admin-review-row-${r.id}`}>
                  <div>
                    <p className="font-medium">{r.author} <span className="ml-2 text-xs tracking-widest text-gold">★★★★★</span></p>
                    <p className="mt-1 text-sm leading-relaxed text-[#A3A3A3]">{r.text}</p>
                  </div>
                  <button
                    onClick={async () => {
                      if (!window.confirm("Eliminare questa recensione?")) return;
                      try {
                        await client.delete(`/admin/reviews/${r.id}`);
                        toast.success("Recensione eliminata");
                        load();
                      } catch {
                        toast.error("Errore durante l'eliminazione");
                      }
                    }}
                    data-testid={`admin-review-delete-${r.id}`}
                    className="shrink-0 border border-white/15 p-2 text-[#A3A3A3] transition-colors hover:border-red-800 hover:text-red-300"
                    aria-label="Elimina"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {tab === "testi" && (
          <section className="mt-8" data-testid="admin-texts-section">
            <div className="border border-gold/30 bg-gold/5 p-5">
              <p className="font-serif text-xl">Modifica i testi del sito</p>
              <p className="mt-2 text-sm leading-relaxed text-[#A3A3A3]">
                Scrivi nei campi qui sotto e premi <strong className="text-gold">Salva i testi</strong>: le modifiche sono subito visibili sul sito. I campi con la parola «in oro» colorano il titolo.
              </p>
            </div>
            {!siteTexts ? (
              <p className="mt-8 text-sm text-[#737373]">Caricamento…</p>
            ) : (
              <>
                <div className="mt-6 grid gap-4">
                  {Object.keys(siteTexts.defaults).map((key) => (
                    <div key={key} className="border border-white/10 p-4" data-testid={`text-field-${key}`}>
                      <label className={labelCls} htmlFor={`text-${key}`}>{siteTexts.labels[key] || key}</label>
                      <textarea
                        id={`text-${key}`}
                        data-testid={`text-input-${key}`}
                        rows={siteTexts.defaults[key].length > 80 ? 3 : 1}
                        value={siteTexts.values[key] ?? ""}
                        onChange={(e) =>
                          setSiteTexts({ ...siteTexts, values: { ...siteTexts.values, [key]: e.target.value } })
                        }
                        className="mt-2 w-full border border-white/15 bg-transparent px-3 py-2.5 text-sm"
                      />
                    </div>
                  ))}
                </div>
                <div className="sticky bottom-4 mt-6 flex justify-end">
                  <button
                    onClick={saveTexts}
                    disabled={savingTexts}
                    data-testid="texts-save-button"
                    className="bg-gold px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-black shadow-[0_10px_40px_-10px_rgba(212,175,55,0.5)] transition-all hover:-translate-y-0.5 hover:bg-gold-hover disabled:opacity-50"
                  >
                    {savingTexts ? "Salvataggio…" : "Salva i testi"}
                  </button>
                </div>
              </>
            )}
          </section>
        )}

        {tab === "promo" && (
          <section className="mt-8" data-testid="admin-promo-section">
            <form onSubmit={verifySpin} className="flex flex-wrap items-center gap-3 border border-gold/30 bg-gold/5 p-5" data-testid="admin-verify-form">
              <TicketCheck size={18} className="text-gold" />
              <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Verifica coupon del cliente</p>
              <input
                data-testid="admin-verify-input"
                value={verifyCode}
                onChange={(e) => { setVerifyCode(e.target.value); setVerifyResult(null); }}
                placeholder="OJ-XXXX-XXXX"
                className="w-48 border border-white/15 bg-transparent px-3 py-2.5 font-mono text-sm uppercase tracking-widest"
              />
              <button type="submit" data-testid="admin-verify-button" className="bg-gold px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-black transition-colors hover:bg-gold-hover">
                Verifica
              </button>
            </form>

            {verifyResult && (
              <div className={`mt-4 border p-5 ${verifyResult.invalid ? "border-red-900/50 bg-red-950/20" : verifyResult.redeemed ? "border-yellow-800/60 bg-yellow-950/20" : "border-emerald-800/60 bg-emerald-950/20"}`} data-testid="admin-verify-result">
                {verifyResult.invalid ? (
                  <p className="text-sm text-red-300">Codice non valido o inesistente.</p>
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <p className="font-medium">{verifyResult.nome} {verifyResult.cognome} <span className="text-sm font-normal text-[#737373]">· {verifyResult.email}</span></p>
                      <p className="mt-1 text-sm text-[#A3A3A3]">{verifyResult.prize_label}</p>
                      <p className="mt-1 font-mono text-sm tracking-widest text-gold">{verifyResult.code}</p>
                      {verifyResult.redeemed && <p className="mt-1 text-xs text-yellow-300">Già riscattato{verifyResult.redeemed_at ? ` il ${new Date(verifyResult.redeemed_at).toLocaleString("it-IT")}` : ""}</p>}
                    </div>
                    {!verifyResult.redeemed && (
                      <button
                        onClick={() => { redeemSpin(verifyResult.code); setVerifyResult({ ...verifyResult, redeemed: true }); }}
                        data-testid="admin-verify-redeem-button"
                        className="bg-gold px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-black transition-colors hover:bg-gold-hover"
                      >
                        Emetti premio
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            <h3 className="mt-10 text-[10px] uppercase tracking-[0.3em] text-[#737373]">Tutti i giri della ruota</h3>
            <div className="mt-4 space-y-3">
              {spins.length === 0 && (
                <p className="border border-white/10 p-8 text-center text-sm text-[#737373]" data-testid="admin-promo-empty">Nessun giro registrato.</p>
              )}
              {spins.map((s) => (
                <div key={s.code} className="flex flex-wrap items-center justify-between gap-4 border border-white/10 p-4" data-testid={`admin-spin-row-${s.code}`}>
                  <div>
                    <p className="font-medium">{s.nome} {s.cognome} <span className="text-sm font-normal text-[#737373]">· {s.email}</span></p>
                    <p className="mt-1 text-sm text-[#A3A3A3]">{s.prize_label}</p>
                    <p className="mt-1 text-xs text-[#737373]">{new Date(s.created_at).toLocaleString("it-IT")}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm tracking-widest text-gold">{s.code}</span>
                    {s.redeemed ? (
                      <span className="border border-emerald-800/60 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-emerald-300" data-testid={`admin-spin-redeemed-${s.code}`}>Riscattato</span>
                    ) : (
                      <button
                        onClick={() => redeemSpin(s.code)}
                        data-testid={`admin-spin-redeem-${s.code}`}
                        className="border border-white/15 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors hover:border-gold hover:text-gold"
                      >
                        Emetti premio
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {editing?.type === "product" && (
        <Modal title={editing.id ? "Modifica prodotto" : "Nuovo prodotto"} onClose={() => setEditing(null)} testid="product-modal">
          <form onSubmit={saveProduct} className="space-y-5" data-testid="product-form">
            <div>
              <label className={labelCls}>Nome *</label>
              <input required data-testid="product-form-name" value={editing.data.name} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, name: e.target.value } })} className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Categoria *</label>
                <select data-testid="product-form-category" value={editing.data.category} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, category: e.target.value } })} className={`${inputCls} bg-[#0d0d0d]`}>
                  {CATEGORIES.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Materiale</label>
                <input data-testid="product-form-material" value={editing.data.material || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, material: e.target.value } })} className={inputCls} placeholder="Oro 18kt" />
              </div>
              <div>
                <label className={labelCls}>Cod. riferimento</label>
                <input data-testid="product-form-reference" value={editing.data.reference || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, reference: e.target.value } })} className={inputCls} placeholder="MU 1155" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Prezzo (€) *</label>
                <input required type="number" step="0.01" min="0" data-testid="product-form-price" value={editing.data.price} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, price: e.target.value } })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Prezzo barrato (€)</label>
                <input type="number" step="0.01" min="0" data-testid="product-form-old-price" value={editing.data.old_price ?? ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, old_price: e.target.value } })} className={inputCls} placeholder="Se in offerta" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Descrizione</label>
              <textarea rows={3} data-testid="product-form-description" value={editing.data.description || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, description: e.target.value } })} className={`${inputCls} resize-none`} />
            </div>
            <GalleryField testid="product-form-gallery" images={editing.data.images || []} onChange={(imgs) => setEditing({ ...editing, data: { ...editing.data, images: imgs } })} />
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm text-[#A3A3A3]">
                <input type="checkbox" data-testid="product-form-featured" checked={!!editing.data.featured} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, featured: e.target.checked } })} className="accent-[#D4AF37]" />
                In evidenza (home)
              </label>
              <label className="flex items-center gap-2 text-sm text-[#A3A3A3]">
                <input type="checkbox" data-testid="product-form-available" checked={!!editing.data.available} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, available: e.target.checked } })} className="accent-[#D4AF37]" />
                Visibile nel catalogo
              </label>
            </div>
            <button type="submit" data-testid="product-form-submit" className="w-full bg-gold py-3 text-[11px] font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-gold-hover">
              Salva prodotto
            </button>
          </form>
        </Modal>
      )}

      {editing?.type === "post" && (
        <Modal title={editing.id ? "Modifica articolo" : "Nuovo articolo"} onClose={() => setEditing(null)} testid="post-modal">
          <form onSubmit={savePost} className="space-y-5" data-testid="post-form">
            <div>
              <label className={labelCls}>Titolo *</label>
              <input required data-testid="post-form-title" value={editing.data.title} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, title: e.target.value } })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Sottotitolo</label>
              <input data-testid="post-form-excerpt" value={editing.data.excerpt || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, excerpt: e.target.value } })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Testo *</label>
              <textarea required rows={5} data-testid="post-form-content" value={editing.data.content} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, content: e.target.value } })} className={`${inputCls} resize-none`} />
            </div>
            <ImageField testid="post-form-image" value={editing.data.image || ""} onChange={(url) => setEditing({ ...editing, data: { ...editing.data, image: url } })} />
            <button type="submit" data-testid="post-form-submit" className="w-full bg-gold py-3 text-[11px] font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-gold-hover">
              Salva articolo
            </button>
          </form>
        </Modal>
      )}
    </main>
  );
}
